import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ScrollSpy.module.scss';

export interface ScrollSpyItem {
  id: string;
  label: string;
  ref: React.RefObject<HTMLDivElement | null>;
}

export interface ScrollSpyProps {
  items: ScrollSpyItem[];
  className?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export const ScrollSpy = ({
  items,
  className,
  containerRef,
}: ScrollSpyProps) => {
  const [activeItem, setActiveItem] = useState<string | null>(
    items[0]?.id || null
  );
  const activeItemRef = useRef<string | null>(activeItem);
  const isScrollingProgrammatically = useRef(false);

  const handleScroll = useCallback(() => {
    if (isScrollingProgrammatically.current) {
      return;
    }

    const container = containerRef?.current;

    if (!container) {
      return;
    }

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const containerHeight = container.offsetHeight;

    const scrollProgress =
      scrollHeight > containerHeight
        ? (scrollTop / (scrollHeight - containerHeight)) * 100
        : 0;

    let currentActiveItem = null;

    const itemPositions = items
      .map(item => {
        const element = item.ref.current;

        if (!element) {
          return null;
        }

        const elementOffsetTop = element.offsetTop;
        const elementHeight = element.offsetHeight;
        const elementBottom = elementOffsetTop + elementHeight;

        const itemStartPercent = (elementOffsetTop / scrollHeight) * 100;
        const itemEndPercent = (elementBottom / scrollHeight) * 100;

        return {
          id: item.id,
          startPercent: itemStartPercent,
          endPercent: itemEndPercent,
          elementOffsetTop,
          elementHeight,
        };
      })
      .filter(Boolean);

    for (const position of itemPositions) {
      if (!position) {
        continue;
      }

      const tolerance = 5;
      const adjustedStartPercent = Math.max(
        0,
        position.startPercent - tolerance
      );

      if (
        scrollProgress >= adjustedStartPercent &&
        scrollProgress < position.endPercent
      ) {
        currentActiveItem = position.id;
        break;
      }
    }

    if (!currentActiveItem && itemPositions.length > 0) {
      const firstItem = itemPositions[0];
      const lastItem = itemPositions[itemPositions.length - 1];

      // If we're at the very beginning, activate first item
      if (scrollProgress <= 5 && firstItem) {
        currentActiveItem = firstItem.id;
      }
      // If we're at the very end, activate last item
      else if (scrollProgress >= 95 && lastItem) {
        currentActiveItem = lastItem.id;
      }
    }

    if (currentActiveItem && currentActiveItem !== activeItemRef.current) {
      activeItemRef.current = currentActiveItem;
      setActiveItem(currentActiveItem);
    }
  }, [items, containerRef]);

  const handleItemClick = (itemId: string) => {
    const item = items.find(item => item.id === itemId);
    const element = item?.ref.current;
    const container = containerRef?.current;

    if (element && container) {
      isScrollingProgrammatically.current = true;

      setActiveItem(itemId);
      activeItemRef.current = itemId;

      const elementOffsetTop = element.offsetTop;

      container.scrollTo({
        top: Math.max(0, elementOffsetTop - 250),
        behavior: 'instant',
      });

      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 1500);
    }
  };

  useEffect(() => {
    const container = containerRef?.current;

    if (!container) {
      return;
    }

    container.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      className={clsx(styles.menuContainer, className)}
      data-testid="scroll-spy-container"
    >
      {items.map(item => {
        const isActive = activeItem === item.id;
        return (
          <div
            key={item.id}
            className={clsx(styles.itemWrapper, {
              [styles.active]: isActive,
            })}
            onClick={() => handleItemClick(item.id)}
            data-testid={`scroll-spy-item-${item.id}`}
          >
            <p
              className={clsx(styles.item, {
                [styles.active]: isActive,
              })}
            >
              {item.label}
            </p>
          </div>
        );
      })}
    </div>
  );
};
